import type { ID, Notes } from '@octopus-do/octopus-project-core'

import { DEFAULT_PAGE_NAME } from '@/constants/defaultNames'
import type { ExportBlockItem, ExportData } from '@/hooks/exportDropdown/types'
import { getBlockContentText, normalizeString } from '@/utils'
import {
  DEFAULT_PROJECT_TITLE,
  DEFAULT_SECTION_TITLE,
  MAIN_SECTION_TITLE,
  XML_SCHEME,
} from './constants'
import type {
  XmlExportColorItem,
  XmlExportControls,
  XmlExportSectionItem,
} from './types'

const HEX_COLOR_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

interface XmlExportContext {
  state: XmlExportControls
  getColorHexCode: (colorId: ID) => string
  getNodeTagTitles: (nodeId: ID) => string[]
}

interface XmlExportProjectData extends XmlExportContext {
  projectTitle: string
  projectId: ID | null
  publishDate: string
  sections: XmlExportSectionItem[]
  tagTitlesByColorHexCode: Map<string, string[]>
  colors?: XmlExportColorItem[]
}

const getXmlText = (value?: string | null): string =>
  normalizeString(value?.trim())?.trim() || ''

const getXmlTag = (tag: string, value?: string, attributes = ''): string => {
  if (!value) {
    return ''
  }

  return `<${tag}${attributes}>${value}</${tag}>`
}

const getAttribute = (name: string, value: string): string => {
  if (!value) {
    return ''
  }

  return ` ${name}="${value}"`
}

export const getHexCode = (value: string): string => {
  const trimmed = value.trim()

  if (!HEX_COLOR_PATTERN.test(trimmed)) {
    return ''
  }

  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
}

const getSeoXml = (notes: Notes): string => {
  const value = [
    getXmlTag('seo_title', getXmlText(notes?.seoTitle)),
    getXmlTag('slug', getXmlText(notes?.seoSlug)),
    getXmlTag('description', getXmlText(notes?.seoDescription)),
    getXmlTag('h1', getXmlText(notes?.seoH1)),
    getXmlTag('keywords', getXmlText(notes?.keywords)),
    getXmlTag('page_intent', getXmlText(notes?.pageIntent)),
  ]

  const note = getXmlText(getBlockContentText(notes?.note))

  if (note) {
    value.push(getXmlTag('note', note))
  }

  return getXmlTag('seo', value.filter(Boolean).join('\n'))
}

const getBlockXml = (
  { state, getColorHexCode }: XmlExportContext,
  block: ExportBlockItem
): string => {
  const value = [getXmlTag('block_title', getXmlText(block.title))]

  if (
    state.isWireframeTypes &&
    block.visual.type === 'wireframes' &&
    block.visual.wireframes.length > 0
  ) {
    value.push(
      getXmlTag('wireframe', getXmlText(block.visual.wireframes.join(',')))
    )
  }

  const blockContent = getXmlText(getBlockContentText(block.content))

  if (state.isNotes && blockContent) {
    value.push(getXmlTag('content', blockContent))
  }

  const colorAttribute = getAttribute('color', getColorHexCode(block.colorId))

  return getXmlTag('block', value.filter(Boolean).join('\n'), colorAttribute)
}

const getBlocksXml = (
  context: XmlExportContext,
  blocks: ExportBlockItem[]
): string[] => {
  if (blocks.length === 0 || context.state.withoutBlocks) {
    return ['<blocks/>']
  }

  const blocksXml = blocks
    .map((block: ExportBlockItem) => getBlockXml(context, block))
    .filter(Boolean)

  if (blocksXml.length === 0) {
    return ['<blocks/>']
  }

  return ['<blocks>', ...blocksXml, '</blocks>']
}

const getNodeXml = (context: XmlExportContext, node: ExportData): string[] => {
  const { state, getColorHexCode, getNodeTagTitles } = context
  const attributes = [
    getAttribute('tag', getXmlText(getNodeTagTitles(node.id).join(','))),
    getAttribute('color', getColorHexCode(node.colorId)),
  ].join('')

  const doc = [
    `<node${attributes}>`,
    getXmlTag('node_title', getXmlText(node.title) || DEFAULT_PAGE_NAME),
  ]

  if (state.isSeoMetaTags) {
    doc.push(getSeoXml(node.notes))
  }

  doc.push(...getBlocksXml(context, node.blocks || []))

  if (node.children && node.children.length > 0) {
    doc.push('<children>')
    node.children.forEach((child: ExportData) => {
      doc.push(...getNodeXml(context, child))
    })
    doc.push('</children>')
  } else {
    doc.push('<children/>')
  }

  doc.push('</node>')

  return doc
}

const getSectionTitle = (title: string, index: number): string =>
  getXmlText(title) ||
  (index === 0 ? MAIN_SECTION_TITLE : DEFAULT_SECTION_TITLE)

const getSectionXml = (
  context: XmlExportContext,
  section: XmlExportSectionItem,
  index: number
): string[] => [
  '<section>',
  getXmlTag('section_title', getSectionTitle(section.title, index)),
  ...section.nodes.flatMap((node: ExportData) => getNodeXml(context, node)),
  '</section>',
]

const getTagsXml = (
  tagTitlesByColorHexCode: Map<string, string[]>
): string[] => {
  if (tagTitlesByColorHexCode.size === 0) {
    return []
  }

  return [
    '<tags>',
    ...Array.from(tagTitlesByColorHexCode.entries()).map(
      ([hexCode, titles]) =>
        `<tag${getAttribute('color', hexCode)}>${getXmlText(titles.join(','))}</tag>`
    ),
    '</tags>',
  ]
}

const getColorsXml = (colors: XmlExportColorItem[]): string[] => {
  const colorsXml = colors
    .map((color) => {
      const hexCode = getHexCode(color.hexCode)

      if (!hexCode) {
        return ''
      }

      const attributes = [
        getAttribute('hex', hexCode),
        getAttribute('show_in_legend', color.showInLegend ? 'true' : 'false'),
      ].join('')

      return `<color${attributes}>${getXmlText(color.title)}</color>`
    })
    .filter(Boolean)

  if (colorsXml.length === 0) {
    return []
  }

  return ['<colors>', ...colorsXml, '</colors>']
}

export const getProjectXml = ({
  projectTitle,
  projectId,
  publishDate,
  sections,
  tagTitlesByColorHexCode,
  colors = [],
  ...context
}: XmlExportProjectData): string => {
  const doc = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<octopus scheme="${XML_SCHEME}">`,
    `<project_title>${getXmlText(projectTitle) || DEFAULT_PROJECT_TITLE}</project_title>`,
    getXmlTag('link', projectId ? `https://octopus.do/${projectId}` : ''),
    `<pubdate>${publishDate}</pubdate>`,
    ...getColorsXml(colors),
    ...getTagsXml(tagTitlesByColorHexCode),
    '<tree>',
    ...sections.flatMap((section: XmlExportSectionItem, index: number) =>
      getSectionXml(context, section, index)
    ),
    '</tree>',
    '</octopus>',
  ]

  return doc.filter(Boolean).join('\n')
}
